/**
 * Canonical Requirements API
 *
 * THE SINGLE SOURCE OF TRUTH FOR EQUIPMENT PRESENCE
 *
 * This module provides the definitive list of equipment that the vessel requires.
 * Both the Requirements UI and Maintenance generation MUST use this API.
 *
 * Architecture:
 * - Requirements Engine: getHardcodedVesselRequirements() filters based on vessel config
 * - This API: Flattens and normalizes the filtered output
 * - Maintenance Generator: Consumes this API, NOT raw domain outputs
 *
 * Key principle: If it doesn't appear in the Requirements Tab UI, it shouldn't generate maintenance.
 */

import { getHardcodedVesselRequirements } from '../../config/hardcodedVesselRequirements.js';

/**
 * Get the canonical list of equipment requirements for a vessel
 * This is the SAME list that the Requirements Tab displays in the UI
 *
 * @param {Object} vesselConfig - Vessel configuration object
 * @returns {Array} Flat array of equipment requirements that should exist on this vessel
 */
export function getCanonicalRequirements(vesselConfig) {
  // Get the filtered requirements from the Requirements Engine
  const requirements = getHardcodedVesselRequirements(vesselConfig);

  // Flatten all equipment from the filtered requirements structure
  // This is the SAME flattening logic the Requirements Tab uses
  const allEquipment = [
    ...(requirements.basicEquipment || []),
    ...(requirements.equipment || []),
    ...(requirements.areaCategoryEquipment || [])
  ].filter(Boolean);

  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {

    // Log FFE specifically for debugging
    const ffeItems = allEquipment.filter(eq => eq.category === 'FFE');

    ffeItems.forEach(item => {

    });
  }

  return allEquipment;
}

/**
 * Get canonical requirements filtered by domain/category
 *
 * @param {Object} vesselConfig - Vessel configuration object
 * @param {string} category - Category filter (e.g., 'FFE', 'LSA', 'Navigation')
 * @returns {Array} Filtered equipment requirements for the specified category
 */
export function getCanonicalRequirementsByCategory(vesselConfig, category) {
  const allEquipment = getCanonicalRequirements(vesselConfig);

  let filtered;
  
  // Special handling for FFE category - include both 'FFE' and 'Fire Extinguisher' categories
  if (category === 'FFE') {
    filtered = allEquipment.filter(eq => 
      eq.category === 'FFE' || 
      eq.category === 'Fire Extinguisher'
    );
    
    if (process.env.NODE_ENV === 'development') {
      const ffeItems = allEquipment.filter(eq => eq.category === 'FFE');
      const extinguisherItems = allEquipment.filter(eq => eq.category === 'Fire Extinguisher');

      // Log the fire extinguisher items specifically
      extinguisherItems.forEach(item => {

      });
    }
  } else {
    // Standard filtering for other categories
    filtered = allEquipment.filter(eq => eq.category === category);
    
    if (process.env.NODE_ENV === 'development') {

    }
  }

  return filtered;
}

/**
 * Check if a specific requirement ID exists in the canonical requirements
 * Belt-and-braces check to ensure equipment is actually required
 *
 * @param {Object} vesselConfig - Vessel configuration object
 * @param {string} requirementId - Requirement ID to check (e.g., 'FFE_v2_ffe.extinguisher.portable')
 * @returns {boolean} True if requirement exists in canonical list
 */
export function requirementExists(vesselConfig, requirementId) {
  const allEquipment = getCanonicalRequirements(vesselConfig);

  const exists = allEquipment.some(eq => eq.id === requirementId);

  if (process.env.NODE_ENV === 'development' && !exists) {

  }

  return exists;
}

/**
 * Get full canonical requirements structure (for UI display)
 * Returns the complete requirements object as returned by the Requirements Engine
 *
 * @param {Object} vesselConfig - Vessel configuration object
 * @returns {Object} Complete requirements object with all categories
 */
export function getCanonicalRequirementsStructure(vesselConfig) {
  return getHardcodedVesselRequirements(vesselConfig);
}

/**
 * Get flattened requirements with WBC3 references for auditing
 * Returns the same data that Step 7 uses, formatted for machine readability
 *
 * @param {Object} vesselConfig - Vessel configuration object
 * @returns {Array} Flattened array of requirement objects with id, name, category, reference
 */
export function getRequirementReferenceSnapshot(vesselConfig) {
  const requirements = getCanonicalRequirements(vesselConfig);
  
  return requirements.map(req => ({
    id: req.id,
    name: req.name || req.label || req.item || '',
    category: req.category || req.domain || '',
    reference: req.reference || req.references || req.regulation || ''
  }));
}

/**
 * Get ALL equipment types by calling domain functions directly
 * This bypasses config filtering and gets every single equipment item defined
 *
 * @returns {Array} Complete list of ALL equipment types
 */
export function getAllEquipmentFromSource() {

  const allEquipment = new Map(); // Deduplicate by ID
  
  // Import all domain equipment functions
  import('../../config/vesselRequirements/domains/basic/index.js').then(basic => {
    import('../../config/vesselRequirements/domains/ffe/index.js').then(ffe => {
      import('../../config/vesselRequirements/domains/navigation/index.js').then(nav => {
        import('../../config/vesselRequirements/domains/gmdss/index.js').then(gmdss => {
          import('../../config/vesselRequirements/domains/lsa/index.js').then(lsa => {
            import('../../config/vesselRequirements/domains/electrical/index.js').then(elec => {
              import('../../config/vesselRequirements/domains/machinery/index.js').then(mach => {
                import('../../config/vesselRequirements/domains/medical/index.js').then(med => {
                  import('../../config/vesselRequirements/domains/environmental/index.js').then(env => {
                    import('../../config/vesselRequirements/domains/operations/index.js').then(ops => {
                      // Process all domain equipment

                    });
                  });
                });
              });
            });
          });
        });
      });
    });
  });
  
  return [];
}

/**
 * Get all unique equipment types using comprehensive config testing
 * Enhanced to include electric/battery and optional equipment
 *
 * @returns {Array} Unique equipment types with their WBC3 references
 */
export function getAllUniqueEquipmentTypes() {

  const uniqueEquipment = new Map(); // Deduplicate by ID
  
  // Comprehensive test configurations to capture ALL equipment variants
  const testConfigs = [
    // Basic small vessel
    { vesselType: 'tug', category: 6, lengthOverall: 8, engineType: 'diesel', enginePower: 50 },
    
    // Medium commercial vessel  
    { vesselType: 'tug', category: 2, lengthOverall: 20, engineType: 'diesel', enginePower: 500, 
      hasAccommodation: true, hasGalley: true, hasMachinerySpace: true },
    
    // Large commercial vessel with high power
    { vesselType: 'cargo', category: 0, lengthOverall: 60, engineType: 'diesel', enginePower: 3000, 
      hasAccommodation: true, hasGalley: true, hasMachinerySpace: true },
    
    // Electric/battery propulsion vessel (THIS WAS MISSING!)
    { vesselType: 'tug', category: 1, lengthOverall: 25, engineType: 'electric', enginePower: 500,
      hasAccommodation: true, hasGalley: true, hasMachinerySpace: true, hasLithiumBatteries: true },
    
    // Hybrid propulsion vessel
    { vesselType: 'passenger', category: 1, lengthOverall: 30, engineType: 'hybrid', enginePower: 1000, 
      hasAccommodation: true, hasGalley: true, passengerCapacity: 50, hasLithiumBatteries: true },
    
    // Vessel with all operations
    { vesselType: 'tug', category: 0, lengthOverall: 40, engineType: 'diesel', enginePower: 2000,
      hasAccommodation: true, hasGalley: true, hasMachinerySpace: true,
      operations: ['towing_operations', 'lifting_operations', 'cargo_operations', 'emergency_response', 
                   'dive_support', 'survey_operations', 'pilot_operations', 'patrol_operations'] },
    
    // Maximum everything config
    { vesselType: 'cargo', category: 0, lengthOverall: 100, engineType: 'hybrid', enginePower: 5000,
      hasAccommodation: true, hasGalley: true, hasMachinerySpace: true, hasLithiumBatteries: true,
      hasGasAppliances: true, hasLPGSystem: true, hasFireRiskArea: true, hasConcentratedElectrical: true,
      hasExhaustLeakRisk: true, hasGasLeakRisk: true, hasEscapeRoute: true, passengerCapacity: 100,
      operations: ['cargo_operations', 'passenger_operations', 'towing_operations', 'lifting_operations', 
                   'dive_support', 'survey_operations', 'pilot_operations', 'patrol_operations', 
                   'research_operations', 'emergency_response', 'pollution_response', 'offshore_support'] }
  ];
  
  testConfigs.forEach((config, i) => {
    try {
      const requirements = getCanonicalRequirements(config);
      requirements.forEach(req => {
        if (req.id && !uniqueEquipment.has(req.id)) {
          uniqueEquipment.set(req.id, {
            id: req.id,
            name: req.name || req.label || req.item || '',
            category: req.category || req.domain || '',
            reference: req.reference || req.references || req.regulation || ''
          });
        }
      });

    } catch (error) {

    }
  });
  
  const results = Array.from(uniqueEquipment.values()).sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));



  // Log missing categories to help debug
  const categories = [...new Set(results.map(r => r.category))];

  return results;
}

// Example canonical requirement shape for reference:
// {
//   id: 'basic_lifejackets',
//   name: 'Personal Flotation Devices',
//   category: 'LSA',
//   reference: 'WBC3 14.1', // <- this is the WBC3 reference field
//   mandatory: true,
//   description: 'Sufficient lifejackets for all persons on board'
// }
